import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

// `marked` is not a sanitizer: its `sanitize` option was removed in v5, so any
// raw HTML in the source markdown is emitted verbatim. Markdown shown in this
// console is LLM output derived from user-supplied workout text, so it must be
// treated as untrusted and scrubbed before it can reach the DOM.
//
// DOMPurify drops <script>, every on* event handler and any javascript: URL by
// default. On top of that we forbid the tags that would let a `data:` URL
// through (DOMPurify allows those on media elements only) plus a few tags that
// have no place in rendered feedback text.
const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: [
    'style', 'form', 'input', 'button', 'img', 'picture', 'source',
    'video', 'audio', 'track', 'iframe', 'object', 'embed'
  ],
  FORBID_ATTR: ['style'],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false
}

// Renders markdown to HTML that is safe to inject. Exported so it can be
// unit tested and reused by any other screen that needs rendered markdown --
// never call `marked.parse` straight into dangerouslySetInnerHTML.
export function renderSafeMarkdown (text) {
  return DOMPurify.sanitize(marked.parse(text || ''), SANITIZE_CONFIG)
}

const SafeMarkdownComponent = ({ text, ...rest }) => {
  // `text` is a complete memo key: renderSafeMarkdown reads nothing else, and
  // SANITIZE_CONFIG is a module constant. Every input that can change the
  // sanitized output also changes `text`, so the cache can never hand back
  // HTML derived from different source markdown -- which matters more here
  // than the saved work, since this is the sanitization boundary.
  const html = useMemo(() => renderSafeMarkdown(text), [text])
  return (
    <div
      {...rest}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

SafeMarkdownComponent.propTypes = {
  text: PropTypes.string
}

export default SafeMarkdownComponent
